import { ChangeEvent, ReactNode, useId } from 'react'

import { Range, RangeLeading, RangeRow, RangeText, SliderContainer, SliderLabel } from './Slider.styles'

interface SliderProps {
  /** Section label rendered above the control (optional). */
  label?: string
  /** Accessible name for the control when no visible `label` is rendered. */
  ariaLabel?: string
  /** Current value, in [min, max]. */
  value: number
  min?: number
  max?: number
  step?: number
  /** Formats the value shown on the left of the track. Defaults to a percentage. */
  formatValue?: (value: number) => string
  /** Shown in place of the formatted value, which then only reaches assistive tech. */
  icon?: ReactNode
  onChange: (value: number) => void
}

const defaultFormat = (value: number): string => `${Math.round(value * 100)}%`

/**
 * Opacity-style slider matching the KML/KMZ design: a section label above a
 * rounded row with the formatted value on the left and a filled range track.
 */
export function Slider(props: SliderProps) {
  const { label, ariaLabel, value, min = 0, max = 1, step = 0.01, formatValue = defaultFormat, icon, onChange } = props
  const id = useId()

  const percent = max > min ? ((value - min) / (max - min)) * 100 : 0

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(Number(event.target.value))
  }

  return (
    <SliderContainer>
      {label && (
        <SliderLabel as="label" htmlFor={id}>
          {label}
        </SliderLabel>
      )}
      <RangeRow>
        {icon ? <RangeLeading>{icon}</RangeLeading> : <RangeText>{formatValue(value)}</RangeText>}
        <Range
          id={id}
          aria-label={ariaLabel}
          aria-valuetext={formatValue(value)}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          percent={percent}
          onChange={handleChange}
        />
      </RangeRow>
    </SliderContainer>
  )
}
